/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Detail Modal Component
 * Feature #117: Operational Dashboards
 * Spec 197: shared Tailwind modal shell — movable + transparent (blocking)
 *           backdrop + frame/shadow; outside-click does NOT dismiss.
 * Constitution: v1.19.0
 */

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { useDraggable } from '../ui/useDraggable';

export interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const DetailModal: React.FC<DetailModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  className = '',
}) => {
  // Movable by the header; drags ignore interactive controls (the close button).
  const { transform, onHandleMouseDown } = useDraggable({
    handleSelector: '[data-modal-drag-handle]',
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Spec 197: transparent backdrop (page stays visible) that still BLOCKS
          input; clicking outside does NOT close (close via ✕ or Escape). */}
      <div className="fixed inset-0 bg-transparent" aria-hidden="true" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          onMouseDown={onHandleMouseDown}
          style={{ transform }}
          className={`relative transform overflow-hidden rounded-lg bg-[var(--color-bg-card)] border border-[var(--color-border)] shadow-2xl transition-all w-full ${sizeClasses[size]} ${className}`}
        >
          {/* Header — drag handle */}
          <div
            data-modal-drag-handle
            className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4 cursor-move select-none"
          >
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)]" id="modal-title">
              {title}
            </h3>
            <button
              onClick={onClose}
              className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
              aria-label="Close modal"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            {children}
          </div>

          {/* Footer (optional, can be provided via children) */}
        </div>
      </div>
    </div>
  );
};
